import { useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import type {
  MonthlyReconciliationReport,
  RegulatoryExtractResponse,
} from '@nadoutong/contracts';
import {
  monthlyReconciliationCsvUrl,
  regulatoryExtractCsvUrl,
  useMonthlyReconciliation,
  useRegulatoryExtract,
} from '../api/reports.ts';
import { usePricing } from '../api/pricing.ts';
import {
  formatUsdMicrosOrCentsAsCnyDisplay,
  formatUsdMicrosOrCentsAsCnyMetered,
} from '../components/formatters.ts';
import { useT } from '../i18n/index.ts';
import {
  KpiCard,
  PageHeader,
  Panel,
  Select,
  TBody,
  THead,
  Table,
  TableEmpty,
  Td,
  Th,
  Tr,
  buttonClass,
} from '../ui/index.ts';

/**
 * `/reports` — compliance + finance reporting. Epic #109 P3.14 + P3.15.
 *
 * Two sections share one month picker at the top:
 *   1. Monthly reconciliation — revenue ledger ↔ paid orders drift.
 *   2. Regulatory extract — per-account monthly usage for PRC AI
 *      filing requirements.
 *
 * Both sections render KPI tiles + table + CSV-download-anchor. The
 * CSV routes are raw GET anchors (download attribute) so the browser
 * attaches the session cookie and streams the file to disk without
 * caching a Blob in React Query.
 */

function fmtCny(cents: number): string {
  const yuan = cents / 100;
  return `¥${yuan.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtUsd(cents: number): string {
  const dollars = cents / 100;
  return `$${dollars.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtInt(n: number): string {
  return n.toLocaleString('en-US');
}

function monthsForPicker(now: Date, count: number): string[] {
  const out: string[] = [];
  const year = now.getFullYear();
  const month = now.getMonth();
  for (let i = 0; i < count; i += 1) {
    const d = new Date(year, month - i, 1);
    if (d.getFullYear() < 2024) break;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    out.push(`${y}-${m}`);
  }
  return out;
}

function defaultMonth(now: Date): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

export function ReportsPage(): JSX.Element {
  const t = useT();
  const now = useMemo(() => new Date(), []);
  const months = useMemo(() => monthsForPicker(now, 12), [now]);
  const [month, setMonth] = useState(() => defaultMonth(now));

  return (
    <section className="flex flex-col gap-6" data-testid="reports-page">
      <PageHeader title={t('reports.title')} sub={t('reports.subtitle')} />
      <div className="flex items-center gap-3" data-testid="reports-month-picker-bar">
        <label
          className="flex items-center gap-2 text-label text-paper-mid"
          htmlFor="reports-month-picker"
        >
          {t('reports.month_label')}
        </label>
        <Select
          value={month}
          onValueChange={setMonth}
          options={months.map((m) => ({ value: m, label: m }))}
          ariaLabel={t('reports.month_label')}
          mono
          className="w-40"
          testId="reports-month-picker"
        />
      </div>

      <MonthlySection month={month} />
      <RegulatorySection month={month} />
    </section>
  );
}

// ─── Monthly reconciliation (P3.14) ──────────────────────────────────

interface MonthSectionProps {
  readonly month: string;
}

function MonthlySection({ month }: MonthSectionProps): JSX.Element {
  const t = useT();
  const query = useMonthlyReconciliation(month);
  return (
    <Panel
      testId="reports-monthly-card"
      caption={t('reports.monthly.title')}
      actions={
        <div className="flex items-center gap-2">
          {/* #1008 drill-through: the paid-orders totals drill into the
              payment-orders ledger behind them. */}
          <Link
            to="/finance/payment-orders"
            data-testid="reports-monthly-orders-link"
            className={buttonClass('ghost', 'sm')}
          >
            {t('reports.monthly.view_orders')}
          </Link>
          <a
            href={monthlyReconciliationCsvUrl(month)}
            download={`monthly-reconciliation-${month}.csv`}
            data-testid="reports-monthly-csv-link"
            className={buttonClass('secondary', 'sm')}
          >
            <Download aria-hidden="true" className="h-3.5 w-3.5" />
            {t('reports.monthly.csv_download')}
          </a>
        </div>
      }
      padded
    >
      <div className="flex flex-col gap-4">
        <p className="m-0 text-dense text-paper-low">{t('reports.monthly.body')}</p>
        {query.isLoading ? (
          <p className="m-0 text-dense text-paper-low" data-testid="reports-monthly-loading">
            {t('reports.monthly.loading')}
          </p>
        ) : query.isError ? (
          <p
            className="m-0 text-dense text-bad"
            role="alert"
            data-testid="reports-monthly-error"
          >
            {t('reports.monthly.error')}
          </p>
        ) : query.data !== undefined ? (
          <MonthlyReport report={query.data} />
        ) : null}
      </div>
    </Panel>
  );
}

interface MonthlyReportProps {
  readonly report: MonthlyReconciliationReport;
}

function MonthlyReport({ report }: MonthlyReportProps): JSX.Element {
  const t = useT();
  const pricing = usePricing();
  const cnyPerUsd = pricing.data?.policy?.cny_per_usd ?? null;
  return (
    <div className="flex flex-col gap-4">
      {report.has_drift ? (
        <div
          className="rounded-lg border border-warn-line bg-warn-dim/50 p-4 text-body text-warn"
          role="alert"
          data-testid="reports-monthly-drift-banner"
        >
          <strong className="font-semibold">{t('reports.monthly.drift.title')}</strong>{' '}
          {t('reports.monthly.drift.body', { amount: fmtCny(report.drift_cny_cents) })}
        </div>
      ) : null}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          testId="reports-monthly-paid"
          label={t('reports.monthly.tile.paid_cny')}
          value={fmtCny(report.paid_orders.cny_cents)}
          delta={{
            text: t('reports.monthly.tile.paid_count', {
              count: fmtInt(report.paid_orders.count),
            }),
            tone: 'neutral',
          }}
        />
        <KpiCard
          testId="reports-monthly-topups"
          label={t('reports.monthly.tile.topups_cny_gross')}
          value={fmtCny(report.topups.cny_gross_cents)}
          delta={{
            text: t('reports.monthly.tile.topups_count', {
              count: fmtInt(report.topups.count),
            }),
            tone: 'neutral',
          }}
        />
        <KpiCard
          testId="reports-monthly-usage-usd"
          label={t('reports.monthly.tile.usage_usd')}
          value={formatUsdMicrosOrCentsAsCnyDisplay(
            report.usage.usd_charged_micros,
            report.usage.usd_charged_cents,
            cnyPerUsd, false)}
          delta={{
            text: t('reports.monthly.tile.usage_rows', {
              rows: fmtInt(report.usage.rows),
            }),
            tone: 'neutral',
          }}
        />
        <KpiCard
          testId="reports-monthly-l2"
          label={t('reports.monthly.tile.l2_profit')}
          value={fmtCny(report.usage.l2_profit_cny_cents)}
          delta={{
            text: t('reports.monthly.tile.l1_profit', {
              amount: fmtCny(report.topups.l1_profit_cny_cents),
            }),
            tone: 'neutral',
          }}
        />
      </div>
      <Table testId="reports-monthly-detail-table">
        <THead>
          <Tr interactive={false}>
            <Th>{t('reports.monthly.detail.metric')}</Th>
            <Th num>{t('reports.monthly.detail.value')}</Th>
          </Tr>
        </THead>
        <TBody>
          <DetailRow
            label={t('reports.monthly.detail.period_start')}
            value={report.period_start}
          />
          <DetailRow
            label={t('reports.monthly.detail.period_end')}
            value={report.period_end}
          />
          <DetailRow
            label={t('reports.monthly.detail.paid_cny_net')}
            value={fmtCny(report.topups.cny_net_cents)}
            tone="ok"
          />
          <DetailRow
            label={t('reports.monthly.detail.usd_credited')}
            value={fmtUsd(report.topups.usd_credited_cents)}
          />
          <DetailRow
            label={t('reports.monthly.detail.ops_adjust_rows')}
            value={fmtInt(report.ops_adjust.rows)}
          />
          <DetailRow
            label={t('reports.monthly.detail.ops_adjust_usd')}
            value={fmtUsd(report.ops_adjust.usd_adjusted_cents)}
          />
          <DetailRow
            label={t('reports.monthly.detail.drift_cny')}
            value={fmtCny(report.drift_cny_cents)}
            tone={report.has_drift ? 'warn' : undefined}
          />
        </TBody>
      </Table>
    </div>
  );
}

// ─── Regulatory extract (P3.15) ──────────────────────────────────────

function RegulatorySection({ month }: MonthSectionProps): JSX.Element {
  const t = useT();
  const query = useRegulatoryExtract(month);
  return (
    <Panel
      testId="reports-regulatory-card"
      caption={t('reports.regulatory.title')}
      actions={
        <a
          href={regulatoryExtractCsvUrl(month)}
          download={`regulatory-extract-${month}.csv`}
          data-testid="reports-regulatory-csv-link"
          className={buttonClass('secondary', 'sm')}
        >
          <Download aria-hidden="true" className="h-3.5 w-3.5" />
          {t('reports.regulatory.csv_download')}
        </a>
      }
      padded
    >
      <div className="flex flex-col gap-4">
        <p className="m-0 text-dense text-paper-low">{t('reports.regulatory.body')}</p>
        {query.isLoading ? (
          <p className="m-0 text-dense text-paper-low" data-testid="reports-regulatory-loading">
            {t('reports.regulatory.loading')}
          </p>
        ) : query.isError ? (
          <p
            className="m-0 text-dense text-bad"
            role="alert"
            data-testid="reports-regulatory-error"
          >
            {t('reports.regulatory.error')}
          </p>
        ) : query.data !== undefined ? (
          <RegulatoryBody response={query.data} />
        ) : null}
      </div>
    </Panel>
  );
}

interface RegulatoryBodyProps {
  readonly response: RegulatoryExtractResponse;
}

function RegulatoryBody({ response }: RegulatoryBodyProps): JSX.Element {
  const t = useT();
  const pricing = usePricing();
  const cnyPerUsd = pricing.data?.policy?.cny_per_usd ?? null;
  return (
    <div className="flex flex-col gap-3">
      {response.truncated ? (
        <div
          className="rounded-lg border border-warn-line bg-warn-dim/50 p-3 text-dense text-warn"
          role="status"
          data-testid="reports-regulatory-truncated-banner"
        >
          {t('reports.regulatory.truncated')}
        </div>
      ) : null}
      <p className="m-0 text-label text-paper-low">
        {t('reports.regulatory.row_count', { count: fmtInt(response.rows.length) })}
      </p>
      <Table testId="reports-regulatory-table">
        <THead>
          <Tr interactive={false}>
            <Th>{t('reports.regulatory.column.account_id')}</Th>
            <Th>{t('reports.regulatory.column.email')}</Th>
            <Th>{t('reports.regulatory.column.registered_at')}</Th>
            <Th num>{t('reports.regulatory.column.input_tokens')}</Th>
            <Th num>{t('reports.regulatory.column.output_tokens')}</Th>
            <Th num>{t('reports.regulatory.column.cost_usd')}</Th>
          </Tr>
        </THead>
        <TBody>
          {response.rows.length === 0 ? (
            <TableEmpty colSpan={6} testId="reports-regulatory-empty">
              {t('reports.regulatory.empty')}
            </TableEmpty>
          ) : (
            response.rows.map((row) => (
              <Tr key={row.account_id} data-testid={`reports-regulatory-row-${row.account_id}`}>
                {/* #1008 drill-through: each per-account regulatory row links
                    to that account's detail (id) and usage overview (email). */}
                <Td mono>
                  <Link
                    to={`/customers/accounts/${encodeURIComponent(row.account_id)}`}
                    className="text-signal transition-colors duration-120 hover:underline"
                    data-testid={`reports-regulatory-account-link-${row.account_id}`}
                  >
                    {row.account_id}
                  </Link>
                </Td>
                <Td emphasis>
                  <Link
                    to={`/business/metrics?tab=usage&usageAccountId=${encodeURIComponent(row.account_id)}`}
                    className="text-paper transition-colors duration-120 hover:text-signal"
                    data-testid={`reports-regulatory-usage-link-${row.account_id}`}
                  >
                    {row.email}
                  </Link>
                </Td>
                <Td mono>{row.registered_at}</Td>
                <Td num emphasis>
                  {fmtInt(row.input_tokens)}
                </Td>
                <Td num emphasis>
                  {fmtInt(row.output_tokens)}
                </Td>
                <Td num emphasis>
                  {formatUsdMicrosOrCentsAsCnyMetered(
                    row.cost_usd_micros,
                    row.cost_usd_cents,
                    cnyPerUsd, false)}
                </Td>
              </Tr>
            ))
          )}
        </TBody>
      </Table>
    </div>
  );
}

// ─── Reusable bits ────────────────────────────────────────────────────

interface DetailRowProps {
  readonly label: string;
  readonly value: string;
  readonly tone?: 'ok' | 'warn';
}

function DetailRow({ label, value, tone }: DetailRowProps): JSX.Element {
  return (
    <Tr interactive={false}>
      <Td>{label}</Td>
      <Td num emphasis={tone === undefined}>
        {tone !== undefined ? (
          <span className={tone === 'ok' ? 'text-ok' : 'font-medium text-warn'}>{value}</span>
        ) : (
          value
        )}
      </Td>
    </Tr>
  );
}
